import {useCallback, useRef} from 'react'

/**
 * Keyboard shortcut handler for the remove-bg editor modal.
 *
 * Shortcuts:
 *  - Escape        → close modal
 *  - Enter         → apply (when editor ready)
 *  - Ctrl/Cmd + Z  → undo
 *  - Ctrl/Cmd + Shift + Z / Ctrl/Cmd + Y → redo
 *  - Space (hold)  → temporarily switch to hand tool
 *
 * @param {{ editor: object, onApply: () => void, onClose: () => void }} params
 * @returns {{ handleKeyDown: (e: KeyboardEvent) => void, handleKeyUp: (e: KeyboardEvent) => void }}
 */
export function useRemoveBgShortcuts({editor, onApply, onClose}) {
    const prevToolRef = useRef('brush')

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                onClose()
                return
            }
            if (e.key === 'Enter' && editor.ready) {
                e.preventDefault()
                onApply()
                return
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault()
                editor.undo()
                return
            }
            if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
                e.preventDefault()
                editor.redo()
                return
            }
            if (e.key === ' ' && !e.repeat) {
                e.preventDefault()
                prevToolRef.current = editor.tool
                editor.setTool('hand')
            }
        },
        [editor, onApply, onClose],
    )

    const handleKeyUp = useCallback(
        (e) => {
            if (e.key === ' ') {
                e.preventDefault()
                editor.setTool(prevToolRef.current)
            }
        },
        [editor],
    )

    return {handleKeyDown, handleKeyUp}
}
